<template>
  <div class="kpi-calendar">
    <!-- 目标日历（企业微信手机端）：上半月历，下半当日目标 -->
    <div class="calendar-body">
      <!-- ============ 上半：日历 ============ -->
      <div class="cal-card">
        <div class="cal-head">
          <div class="cal-title">{{ monthText }}</div>
          <div class="cal-nav">
            <!--
              员工：看谁的日历。
              ★ 必须传 display-text：组件只认「当前选中项」或外部传入的回显文案，
                默认值是登录人时并没有走过选中动作，不补文案这里会显示成空白的「员工」。
            -->
            <mobile-search-select v-model="employeeId" :query="queryStaffPage" size="small"
                                  label-column="employeeName" code-column="employeeId"
                                  placeholder="员工" search-placeholder="员工姓名"
                                  :display-text="employeeName" @change="onEmployeeChange">
              <!-- 职员按「头像 + 名称 / 手机号」两行显示（预览模板 B） -->
              <template #item="{item}">
                <div class="mss-item mss-item-avatar-row">
                  <div class="mss-item-avatar">
                    <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
                    <yq-icon v-else name="element-ui_user"/>
                  </div>
                  <div class="mss-item-info">
                    <div class="mss-item-main">{{ item.employeeName }}</div>
                    <div class="mss-item-sub">{{ item.employeePhone || "无联系电话" }}</div>
                  </div>
                </div>
              </template>
            </mobile-search-select>
            <span class="nav-btn" @click="shiftMonth(-1)">‹</span>
            <!-- 「回到本月」按钮标出当前看的是几月；点它才跳回真实的本月 -->
            <span class="nav-btn nav-today" @click="toThisMonth">{{ viewMonthText }}</span>
            <span class="nav-btn" @click="shiftMonth(1)">›</span>
          </div>
        </div>
        <div class="cal-legend">
          <span><i class="dot today"></i>今天</span>
          <span><i class="dot task"></i>有任务</span>
          <span><i class="dot done"></i>全部完成</span>
          <span><i class="dot overdue"></i>漏填日志</span>
        </div>
        <div class="cal-grid">
          <div v-for="d in weekDays" :key="d" class="cal-dow">{{ d }}</div>
          <template v-for="(cell, index) in gridCells" :key="index">
            <div v-if="!cell" class="cal-blank"></div>
            <div v-else class="cal-day"
                 :class="{today: cell.isToday, selected: cell.isSelected, overdue: cell.overdue}"
                 @click="selectDay(cell.date)">
              <span class="cal-date">{{ cell.day }}</span>
              <span v-if="cell.taskCount" class="cal-count" :class="cell.allDone ? 'green' : 'blue'">{{ cell.taskCount }}</span>
              <!-- 放假/调休：右下角标出节日名或「调休」二字 -->
              <span v-if="cell.holidayText" class="cal-holiday" :class="cell.holidayType">{{ cell.holidayText }}</span>
            </div>
          </template>
        </div>
      </div>

      <!-- ============ 下半：当日目标任务 ============ -->
      <div class="day-title">当日目标 · {{ selectedDateLabel }}</div>

      <!-- 工作日志：每日一条，只读展示；点右边的「写日志」进编辑页 -->
      <!-- ★ 必须 show-title=false：不给标题时 ListCard 仍会渲染一条 60px 高的空标题行 -->
      <list-card class="log-card" type="form" :show-title="false">
        <div class="log-head">
          <span class="log-label">工作日志</span>
          <span class="write-log-btn" @click="toLog">写日志</span>
        </div>
        <div v-if="dayLog" class="log-text">{{ dayLog }}</div>
        <div v-else class="log-empty">当天还没有工作日志</div>
      </list-card>

      <!--
        整张卡点开就是编辑：手机上在卡片右上角塞两个按钮太挤、也容易点错，
        所以只留「执行」一个按钮，编辑改由点卡片本身触发（卡片下方有提示文案）。
      -->
      <list-card class="task-card" v-for="item in goals" :key="item.id" :label="item.title" @click="toEdit(item)">
        <template #desc>
          <!--
            执行：记录这条日程办没办完、花了多久（原来这里是「写日志」，日志按天记，不该挂到每条任务上）。
            ★ 已完成是终态：连按钮带箭头一起收起来，卡片也不再响应点击（见 isDone / toEdit）
          -->
          <span v-if="!isDone(item)" class="task-act primary" @click.stop="toExecute(item)">执行</span>
          <yq-icon v-if="!isDone(item)" class="item-arrow" name="element-ui_arrow-right"/>
        </template>
        <div v-if="!isDone(item)" class="task-hint">点击记录以打开修改页面</div>
        <data-column label="类型">{{ typeName(item.calendarType) }}</data-column>
        <data-column label="状态">
          <span class="status-tag" :class="'status-' + (item.kpiStatus || 0)">{{ statusName(item.kpiStatus) }}</span>
        </data-column>
        <data-column label="完成情况">{{ item.result || "—" }}</data-column>
        <data-column label="消耗工时">{{ item.hours ? item.hours + " h" : "—" }}</data-column>
        <data-column v-if="item.remark" label="备注">{{ item.remark }}</data-column>
      </list-card>
      <div v-if="!goals.length" class="day-empty">当天没有目标</div>
    </div>

    <!-- 新建日程：统一悬浮按钮，点开是一个独立页面 -->
    <mobile-float-button label="新建日程" icon="element-ui_plus" @click="toScheduleForm"/>


  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileFloatButton from "@/components/qywx/button/MobileFloatButton.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import calendarApi from "@/api/dz-crm/kpi/calendarApi";
import calendarHolidayApi from "@/api/dz-crm/kpi/calendarHolidayApi";
import util from "@/util/util";
import {
  TYPE_LOG, typeName, statusName, today, dateOnly, monthLabel, buildGridCells
} from "@/views/h5/crm/kpi/kpiCalendarDictionary";

/** 日程状态：已完成。终态，卡片上不再给任何可操作的入口 */
const STATUS_DONE = 100;

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "KC",
  components: {
    YqIcon, DataColumn, ListCard, MobileFloatButton, MobileSearchSelect
  },
  data() {
    const now = new Date();
    return {
      weekDays: ["日", "一", "二", "三", "四", "五", "六"],
      employeeId: "",
      employeeName: "", // 员工选择框的回显文案（默认是登录人，见 created）
      month: now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0"),
      selectedDate: "",
      // 当月全部 kpi日历行：日历格子与当日详情都用它
      rows: [],
      // 当年的放假/调休，键为 yyyy-MM-dd：格子标识与「往日落填日志」判定共用
      holidayMap: {},
      pageTitle: "",
    };
  },
  computed: {
    /**
     * 路由上的 date 参数（yyyy-MM-dd），消息推送里跳过来时用它定位到具体某天。
     *
     * ★ 必须验格式：月份、日期都是从这个串上截出来的，
     *   传个乱七八糟的值（或数组）会把月份也带偏，页面直接看不到数据。
     */
    routeDate() {
      const date = dateOnly(this.route.query.date);
      return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
    },
    monthText() {
      return monthLabel(this.month);
    },
    /**
     * 「回到本月」按钮上的文案：当前正在看的月份（如显示 9 月日历时显示「9月」）。
     * ★ 跟着 month 走，不取真实日期 —— 按钮写的是「你现在在看哪个月」，
     *   点它才跳到真实的本月，跳完这个文案自然也跟着变。
     */
    viewMonthText() {
      if (!this.month) {
        return "";
      }
      return Number(this.month.split("-")[1]) + "月";
    },
    /** 当日目标标题里的日期：只到「月-日」 */
    selectedDateLabel() {
      return this.selectedDate ? this.selectedDate.slice(5) : "";
    },
    gridCells() {
      return buildGridCells(this.month, this.rows, this.selectedDate, today(), this.holidayMap);
    },
    /** 当天除日志以外的行 = 要展示的「目标」 */
    goals() {
      return (this.rows || [])
          .filter(row => dateOnly(row.goalDate) === this.selectedDate && Number(row.calendarType) !== TYPE_LOG);
    },
    /** 当天的日志内容（每日一条） */
    dayLog() {
      const logRow = (this.rows || [])
          .find(row => dateOnly(row.goalDate) === this.selectedDate && Number(row.calendarType) === TYPE_LOG);
      return logRow ? logRow.result : "";
    }
  },
  async created() {
    this.pageTitle = document.title;
    document.title = "目标日历";
    // 默认看自己、看今天（与 web 端一致）
    const userInfo = await util.getUserInfo();
    this.employeeId = (userInfo && userInfo.id) || "";
    this.employeeName = (userInfo && userInfo.employeeName) || "";
    // 带 date 参数（从消息推送点进来的）则直接落到那一天
    this.selectedDate = this.routeDate || today();
    // 选中的日期不在本月时把月份跟过去，否则打开的页面停在别的月份、看不到这一天
    if (this.routeDate) {
      this.month = this.routeDate.slice(0, 7);
    }
    this.loadHoliday();
    this.loadMonth();
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    typeName(type) {
      return typeName(type);
    },
    statusName(status) {
      return statusName(status);
    },
    /** 员工选择框的数据源：平台职员一次返回，包一层适配通用选择组件 */
    queryStaffPage(params) {
      return calendarApi.queryEmployeeStaff({}).then(list => {
        const keyword = (params.searchKey || "").trim();
        const records = keyword
            ? (list || []).filter(item => (item.employeeName || "").indexOf(keyword) > -1)
            : (list || []);
        return {records, pageCount: 1};
      });
    },
    /** 当年的放假/调休。翻年时换一份，跨年那几天才不会用错数据 */
    loadHoliday() {
      const year = Number(this.month.split("-")[0]);
      calendarHolidayApi.queryByYear(year).then(list => {
        const map = {};
        (list || []).forEach(item => map[dateOnly(item.holidayDate)] = item);
        const kept = {};
        Object.keys(this.holidayMap).forEach(date => {
          if (!date.startsWith(String(year))) {
            kept[date] = this.holidayMap[date];
          }
        });
        this.holidayMap = Object.assign(kept, map);
      }).catch(() => {
      });
    },
    /** 当月日历 + 当日工作一起刷（换人、切月、存完日程都走这里） */
    loadMonth() {
      // 没拿到员工就不查：后端对空 employeeId 不做过滤，会把整个商户所有人的行都返回，
      // 界面上变成「大家的目标混在一起」。与 web 端同一处理
      if (!this.employeeId) {
        this.rows = [];
        return;
      }
      // queryMonth 返回的就是当月的日历行数组（与 web 端用法一致），
      // 不能按 {calendarList: [...]} 取 —— 那样永远拿到空数组，页面会一直显示「当天没有目标」
      calendarApi.queryMonth({employeeId: this.employeeId, month: this.month}).then(list => {
        this.rows = list || [];
      });
    },
    /** 切换员工：清掉预置的登录人回显，由组件显示新选中的人 */
    onEmployeeChange() {
      this.employeeName = "";
      this.loadMonth();
    },
    /** 切某一天：只换选中日期，当月数据已经拿全了 */
    selectDay(date) {
      this.selectedDate = date;
    },
    shiftMonth(step) {
      const parts = this.month.split("-").map(Number);
      const date = new Date(parts[0], parts[1] - 1 + step, 1);
      this.month = date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0");
      this.loadHoliday();
      this.loadMonth();
    },
    toThisMonth() {
      const now = new Date();
      this.month = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
      this.selectedDate = today();
      this.loadHoliday();
      this.loadMonth();
    },
    // ---------- 新建日程 ----------
    /** 日程是「某天的一件事」，只落一行；表单在独立页面里填 */
    toScheduleForm() {
      this.router.push({
        path: "/qywx/kpi/calendar/schedule",
        // 带去员工与日期：日程是记在某个人的某一天上的
        query: {employeeId: this.employeeId, date: this.selectedDate},
      });
    },
    // ---------- 跳转 ----------
    /** 写日志：日志按天记，带上员工与日期 */
    toLog() {
      this.router.push({
        path: "/qywx/kpi/calendar/log",
        query: {employeeId: this.employeeId, date: this.selectedDate},
      });
    },
    /**
     * 跳日程相关页面。
     *
     * ★ 一律带上 employeeId 与 date：那几个页面没有「按 id 查一条日历」的接口，
     *   只能按「员工 + 日期」把当天拉回来再挑（见 KpiCalendarDetail#findById）。
     *   之前只传 id，详情页拿不到员工和日期就直接返回，于是提示「没有找到这条目标任务」。
     */
    toCalendarPage(path, item) {
      this.router.push({
        path: path,
        query: {id: item.id, employeeId: this.employeeId, date: dateOnly(item.goalDate)},
      });
    },
    /** 已完成(100)是终态：不再显示执行按钮，也不再进编辑页/执行页 */
    isDone(item) {
      return Number(item.kpiStatus) === STATUS_DONE;
    },
    /** 执行：记录办没办完、花了多久、有什么要说的 */
    toExecute(item) {
      if (this.isDone(item)) {
        return;
      }
      this.toCalendarPage("/qywx/kpi/calendar/execute", item);
    },
    /**
     * 编辑：改日程本身（类型、关联客户、标题、日期、备注）。点整张卡片走这里。
     * ★ 卡片的 @click 一直都在（模板里不做条件绑定更好读），已完成在这里挡住
     */
    toEdit(item) {
      if (this.isDone(item)) {
        return;
      }
      this.toCalendarPage("/qywx/kpi/calendar/schedule", item);
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-calendar{
  height: 100%;
  display: flex;
  flex-direction: column;
  // 悬浮按钮的定位祖先
  position: relative;
  background: var(--yq-background-color);
}
.calendar-body{
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 90px;
}
// ---------------- 日历 ----------------
.cal-card{
  background: #FFF;
  border-radius: 6px;
  padding: 10px;
  box-shadow: 0 0 10px var(--yq-shadow);
  .cal-head{
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
    .cal-title{
      font-size: var(--yq-font-size-large-mobile);
      font-weight: 600;
      color: var(--yq-font-color-base);
    }
    .cal-nav{
      display: flex;
      align-items: center;
      gap: 12px;
      .nav-btn{
        min-width: 26px;
        height: 26px;
        line-height: 26px;
        text-align: center;
        border-radius: 50%;
        color: var(--el-color-primary);
        background: var(--el-color-primary-light-9);
        font-size: var(--yq-font-size-mini-mobile);
        // 「回到本月」要显示月份（如 09月），比左右箭头宽，做成药丸形而不是正圆
        &.nav-today{
          padding: 0 8px;
          border-radius: 13px;
          white-space: nowrap;
        }
      }
    }
  }
  .cal-legend{
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding-bottom: 8px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--yq-font-color-desc);
    span{
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .dot{
      width: 8px;
      height: 8px;
      border-radius: 50%;
      &.today{
        background: var(--el-color-primary);
      }
      &.task{
        background: #409eff;
      }
      &.done{
        background: #67c23a;
      }
      &.overdue{
        background: #f56c6c;
      }
    }
  }
  // 网格：像 web 一样带边框（表格观感），所以格子不再用圆角
  .cal-grid{
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-top: 1px solid var(--yq-border-color);
    border-left: 1px solid var(--yq-border-color);
    .cal-dow{
      text-align: center;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-desc);
      border-right: 1px solid var(--yq-border-color);
      border-bottom: 1px solid var(--yq-border-color);
      padding: 4px 0;
    }
    .cal-blank{
      height: 46px;
      border-right: 1px solid var(--yq-border-color);
      border-bottom: 1px solid var(--yq-border-color);
    }
    .cal-day{
      position: relative;
      height: 46px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border-right: 1px solid var(--yq-border-color);
      border-bottom: 1px solid var(--yq-border-color);
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      // 今天：蓝底白字（与 web 端一致）
      &.today{
        background: var(--el-color-primary);
        color: #FFF;
        font-weight: 700;
        .cal-count{
          background: #FFF;
          color: var(--el-color-primary);
        }
      }
      // 其它被选中的「天」：下面中间点一个小圆点。选中的正好是今天时上面的样式生效，这里不再叠加
      &.selected:not(.today){
        color: var(--el-color-primary);
        &::after{
          content: "";
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: var(--el-color-primary);
        }
      }
      &.overdue{
        color: #f56c6c;
      }
      .cal-holiday{
        position: absolute;
        right: 2px;
        bottom: 2px;
        font-size: 10px;
        line-height: 1;
        &.off{
          color: #f56c6c;
        }
        &.work{
          color: var(--yq-font-color-desc);
        }
      }
      &.today .cal-holiday{
        color: #FFF;
      }
      .cal-count{
        margin-top: 2px;
        min-width: 14px;
        padding: 0 4px;
        height: 14px;
        line-height: 14px;
        border-radius: 7px;
        text-align: center;
        font-size: 10px;
        color: #FFF;
        &.blue{
          background: #409eff;
        }
        &.green{
          background: #67c23a;
        }
      }
    }
  }
}
// ---------------- 当日目标 ----------------
.day-title{
  margin: 14px 0 0;
  font-size: var(--yq-font-size-mini-mobile);
  font-weight: 600;
  color: var(--yq-font-color-base);
}
.log-card{
  .log-head{
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .log-label{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
  // 写日志：蓝色文字，提示这里可以点（原先是默认黑色，看着像静态文案）
  .write-log-btn{
    flex: none;
    color: var(--el-color-primary);
    white-space: nowrap;
  }
  .log-text{
    margin-top: 6px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-all;
  }
  .log-empty{
    margin-top: 6px;
    color: var(--yq-font-color-desc);
  }
}
.task-card{
  :deep(.card-title){
    // 任务标题通常较长，右上角又有写日志按钮和箭头，允许折两行
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  // 写日志：小描边按钮，放在卡片右上
  .write-log-btn{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    border: 1px solid var(--el-color-primary);
    white-space: nowrap;
  }
  // 卡片右上的「执行」按钮：实心主色，卡片上唯一的按钮
  .task-act{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    border: 1px solid var(--el-color-primary);
    white-space: nowrap;
    &.primary{
      color: #FFF;
      background: var(--el-color-primary);
    }
  }
  // 名称下方的操作提示：浅色小字，说明「点这张卡能改」
  .task-hint{
    margin-bottom: 6px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--yq-font-color-desc);
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
}
.status-tag{
  padding: 2px 8px;
  border-radius: 4px;
  color: var(--el-color-info);
  background: var(--el-fill-color-light);
  &.status-50{
    color: var(--el-color-warning);
    background: var(--el-color-warning-light-9);
  }
  &.status-100{
    color: var(--el-color-success);
    background: var(--el-color-success-light-9);
  }
}
.day-empty{
  margin-top: 10px;
  padding: 20px;
  text-align: center;
  color: var(--yq-font-color-desc);
  background: #FFF;
  border-radius: 6px;
  box-shadow: 0 0 10px var(--yq-shadow);
}
</style>
